import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { AlertTriangle, Calendar, DollarSign, Users, FileText, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function YearEndClosing() {
  const { token } = useAuth();
  const [closings, setClosings] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [formData, setFormData] = useState({
    closing_year: '',
    new_year: '',
    new_year_name: '',
    promote_students: true,
    carry_forward_balances: true,
    notes: ''
  });

  useEffect(() => {
    fetchClosings();
    fetchAcademicYears();
  }, []);

  const fetchClosings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finance/year-end-closings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setClosings(data);
    } catch (error) {
      toast.error('Failed to fetch year-end closings');
    } finally {
      setLoading(false);
    }
  };

  const fetchAcademicYears = async () => {
    try {
      const res = await fetch(`${API_URL}/api/academic-years`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAcademicYears(data);
    } catch (error) {
      console.error('Failed to fetch academic years');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.closing_year || !formData.new_year) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!window.confirm(`Are you sure you want to close year ${formData.closing_year}? This action cannot be undone.`)) {
      return;
    }

    setProcessing(true);

    try {
      const res = await fetch(`${API_URL}/api/finance/year-end-closing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (res.ok) {
        toast.success('Year-end closing completed successfully');
        setShowDialog(false);
        resetForm();
        fetchClosings();
        fetchAcademicYears();
      } else {
        toast.error(data.detail || 'Year-end closing failed');
      }
    } catch (error) {
      toast.error('Failed to process year-end closing');
    } finally {
      setProcessing(false);
    }
  };

  const resetForm = () => {
    const currentYear = new Date().getFullYear();
    setFormData({
      closing_year: currentYear.toString(),
      new_year: (currentYear + 1).toString(),
      new_year_name: `Academic Year ${currentYear + 1}`,
      promote_students: true,
      carry_forward_balances: true,
      notes: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(amount);
  };

  const currentYear = academicYears.find(y => y.is_current);
  const openYears = academicYears.filter(y => !y.is_closed);

  return (
    <div className="space-y-6" data-testid="year-end-closing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Year-End Closing</h1>
          <p className="text-slate-500">Close the fiscal year and transfer records</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowDialog(true); }} 
          variant="destructive"
          data-testid="start-closing-btn"
        >
          <Calendar className="w-4 h-4 mr-2" /> Start Year-End Closing
        </Button>
      </div>

      {/* Current Year Info */}
      <Card className="border-l-4 border-l-indigo-500">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Current Academic Year</p>
              <p className="text-2xl font-bold text-indigo-600">
                {currentYear ? currentYear.name : 'No year set'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {currentYear?.is_closed ? (
                <Badge variant="secondary">Closed</Badge>
              ) : (
                <Badge variant="default">Active</Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Warning Card */}
      <Card className="bg-yellow-50 border-yellow-200">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-yellow-800">Important: Year-End Closing Process</h3>
              <ul className="mt-2 text-sm text-yellow-700 space-y-1">
                <li>• Calculates Profit & Loss and transfers net income to Retained Earnings</li>
                <li>• Resets all Revenue and Expense account balances to zero</li>
                <li>• Creates a new Academic Year and sets it as current</li>
                <li>• Optionally promotes students to the next class level</li>
                <li>• Outstanding invoice balances are carried forward</li>
                <li>• <strong>This process cannot be reversed</strong></li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Past Closings */}
      <Card>
        <CardHeader>
          <CardTitle>Past Year-End Closings</CardTitle>
          <CardDescription>History of completed year-end closings</CardDescription>
        </CardHeader>
        <CardContent>
          {closings.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Closed Year</TableHead>
                  <TableHead>New Year</TableHead>
                  <TableHead>Closing Date</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Expenses</TableHead>
                  <TableHead className="text-right">Net Income</TableHead>
                  <TableHead className="text-center">Students Promoted</TableHead>
                  <TableHead className="text-right">Outstanding Fees</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {closings.map(closing => (
                  <TableRow key={closing.id}>
                    <TableCell className="font-bold">{closing.closing_year}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                        {closing.new_year}
                      </div>
                    </TableCell>
                    <TableCell>{closing.closing_date}</TableCell>
                    <TableCell className="text-right text-green-600">{formatCurrency(closing.total_revenue)}</TableCell>
                    <TableCell className="text-right text-red-600">{formatCurrency(closing.total_expenses)}</TableCell>
                    <TableCell className={`text-right font-bold ${closing.net_income >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(closing.net_income)}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{closing.students_promoted} promoted</Badge>
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(closing.outstanding_balance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No year-end closings performed yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Year-End Closing Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Year-End Closing
            </DialogTitle>
            <DialogDescription>
              This will permanently close the financial year and create a new academic year.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Year to Close *</Label>
                <Input
                  value={formData.closing_year}
                  onChange={(e) => setFormData(prev => ({ ...prev, closing_year: e.target.value }))}
                  placeholder="2025"
                  required
                />
              </div>
              <div>
                <Label>New Year *</Label>
                <Input
                  value={formData.new_year}
                  onChange={(e) => setFormData(prev => ({ ...prev, new_year: e.target.value }))}
                  placeholder="2026"
                  required
                />
              </div>
            </div>

            <div>
              <Label>New Year Name *</Label>
              <Input
                value={formData.new_year_name}
                onChange={(e) => setFormData(prev => ({ ...prev, new_year_name: e.target.value }))}
                placeholder="Academic Year 2026"
                required
              />
            </div>

            <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Checkbox 
                  id="promote_students"
                  checked={formData.promote_students}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, promote_students: checked }))}
                />
                <Label htmlFor="promote_students" className="cursor-pointer">
                  Promote students to next class level
                </Label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox 
                  id="carry_forward"
                  checked={formData.carry_forward_balances}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, carry_forward_balances: checked }))}
                />
                <Label htmlFor="carry_forward" className="cursor-pointer">
                  Carry forward outstanding fee balances
                </Label>
              </div>
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes about this year-end closing..."
                rows={3}
              />
            </div>

            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-700">
                  <p className="font-semibold">Warning: This action is irreversible</p>
                  <p className="mt-1">All revenue and expense accounts will be reset to zero. Make sure all financial data has been reconciled before proceeding.</p>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} disabled={processing}>
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={processing} data-testid="confirm-closing-btn">
                {processing ? 'Processing...' : 'Close Year'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
